/**
 * Stateful harnesses for the ds/ catalog specimens.
 *
 * Separate from componentCatalog.ds.tsx because that file's default export is
 * data, and react-refresh requires a module to export only components or only
 * values — mixing them silently breaks fast refresh for the whole file.
 */
import { useState } from 'react';
import { DataTable, Checkbox, ChoiceGroup, Switch, Modal, Disclosure, DisclosureGroup, Tabs, TabPanel, ProgressBar, type SortSpec } from '../../components/ds';
import { btnPrimary, btnSecondary } from '../../components/mono/primitives';
import { Settings, Play, Flag } from 'lucide-react';

import { ROWS, COLUMNS } from './componentCatalog.fixtures';

/* ── Stateful example harnesses ─────────────────────────────────────────── */

export function SortableSelectableTable() {
  const [sort, setSort] = useState<SortSpec | null>({ key: 'score', direction: 'desc' });
  const [selected, setSelected] = useState<Set<string>>(new Set(['1']));
  const sorted = [...ROWS].sort((a, b) =>
    sort?.key === 'score' ? (sort.direction === 'asc' ? a.score - b.score : b.score - a.score) : 0
  );
  return (
    <DataTable
      columns={COLUMNS}
      rows={sorted}
      getRowKey={(r) => r.id}
      sort={sort}
      onSortChange={setSort}
      selectable
      selectedKeys={selected}
      onSelectionChange={setSelected}
      footer={<span className="font-mono text-xs uppercase tracking-[0.12em]">{selected.size} selected</span>}
    />
  );
}

export function SwitchDemo() {
  const [on, setOn] = useState(true);
  const [off, setOff] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <Switch checked={on} onChange={setOn} label="Waiting room" description="Hold participants until the facilitator opens the session." />
      <Switch checked={off} onChange={setOff} label="Allow response changes" />
      <Switch checked={false} onChange={() => {}} label="Locked by policy" disabled />
    </div>
  );
}

export function CheckboxDemo() {
  const [checked, setChecked] = useState(true);
  return (
    <ChoiceGroup legend="Notify on" hint="Applies to every session in this cycle.">
      <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} label="Ratification" description="When a proposal becomes binding." />
      <Checkbox defaultChecked={false} label="Every toss" />
      <Checkbox indeterminate label="Blocked items" description="Some, not all, of this group is selected." />
    </ChoiceGroup>
  );
}


export function ModalDemo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} {...btnSecondary()}>
        Open dialog
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        label="Cycle 14"
        title="Retire this session?"
        description="Its results stay on the record. The session stops accepting responses."
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} {...btnSecondary()}>Cancel</button>
            <button type="button" onClick={() => setOpen(false)} {...btnPrimary()}>Retire</button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-[var(--text-sec)]">
          Tab cycles inside this dialog, Escape closes it, and focus returns to the button that
          opened it — the last of which hand-rolled modals never do.
        </p>
      </Modal>
    </>
  );
}

export function DisclosureDemo() {
  return (
    <DisclosureGroup className="w-full">
      <Disclosure title="Participants" label="Configure" meta="14" defaultOpen>
        <p className="text-sm text-[var(--text-sec)]">Body content. Unmounted while closed.</p>
      </Disclosure>
      <Disclosure title="Criteria" label="Configure" meta="6">
        <p className="text-sm text-[var(--text-sec)]">Six criteria inherited from the track.</p>
      </Disclosure>
      <Disclosure title="Blocked dependencies" label="Outcomes" accent="coral" meta="2">
        <p className="text-sm text-[var(--text-sec)]">Two items cannot be ranked yet.</p>
      </Disclosure>
    </DisclosureGroup>
  );
}

export function TabsDemo() {
  const [active, setActive] = useState('setup');
  return (
    <div className="w-full">
      <Tabs
        ariaLabel="Session sections"
        active={active}
        onChange={setActive}
        groups={[
          { label: 'Launch', tone: 'text-teal-500', tabs: [
            { id: 'setup', label: 'Setup', icon: Settings },
            { id: 'run', label: 'Run', icon: Play },
          ] },
          { label: 'Outcomes', tone: 'text-amber-500', tabs: [
            { id: 'results', label: 'Results', icon: Flag, count: 4 },
          ] },
        ]}
      />
      <TabPanel id="setup" active={active} className="p-4 text-sm text-[var(--text-sec)]">
        Setup panel. Arrow keys move between tabs; only the active tab is in the tab order.
      </TabPanel>
      <TabPanel id="run" active={active} className="p-4 text-sm text-[var(--text-sec)]">
        Run panel.
      </TabPanel>
      <TabPanel id="results" active={active} className="p-4 text-sm text-[var(--text-sec)]">
        Results panel.
      </TabPanel>
    </div>
  );
}

export function ProgressDemo() {
  return (
    <div className="flex w-full max-w-sm flex-col gap-4">
      <ProgressBar label="Responses in" value={9} max={14} showValue tone="slate" />
      <ProgressBar label="Capacity used" value={112} max={100} showValue tone="coral"
        format={(v, m) => `${Math.round((v / m) * 100)}%`} />
      <ProgressBar label="Ratified" value={4} max={4} showValue tone="green" size="sm" />
    </div>
  );
}
